import { useEffect, useCallback } from "react";
import { useProfile } from "@/hooks/useProfile";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { isNative, getPlatform } from "@/lib/platform";
import { PushNotifications } from "@capacitor/push-notifications";

export const useNotifications = () => {
    const { profile, loading: profileLoading } = useProfile();
    const isApp = isNative();

    // 1. SAVE TOKEN TO SUPABASE
    const saveToken = useCallback(async (token) => {
        if (!profile?.id) return;
        const platform = getPlatform();

        try {
            const { error } = await supabase
                .from('push_tokens')
                .upsert({
                    user_id: profile.id,
                    token: token,
                    platform: platform
                }, { onConflict: 'user_id,token' });

            if (error) console.error("Failed to register push token:", error);
        } catch (e) {
            console.error(e);
        }
    }, [profile]);

    // 2. SETUP NATIVE PUSH
    const setupNativePush = useCallback(async () => {
        if (!isApp) return;

        try {
            let permStatus = await PushNotifications.checkPermissions();

            if (permStatus.receive === 'prompt') {
                permStatus = await PushNotifications.requestPermissions();
            }

            if (permStatus.receive !== 'granted') {
                console.warn("User denied push permissions");
                return;
            }

            // Clear existing listeners to prevent duplicates/memory leaks
            await PushNotifications.removeAllListeners();

            // Clear delivered notifications tray on launch
            try {
                await PushNotifications.removeAllDeliveredNotifications();
            } catch (e) {
                console.error("Failed to clear notification center tray:", e);
            }

            // Listeners
            PushNotifications.addListener('registration', async (token) => {
                const platform = getPlatform();
                if (platform === 'ios') {
                    try {
                        const { FCM } = await import("@capacitor-community/fcm");
                        const fcmToken = await FCM.getToken();
                        saveToken(fcmToken.token);
                    } catch (e) {
                        console.error("[FCM] Failed to convert token on iOS:", e);
                        saveToken(token.value); // Fallback
                    }
                } else {
                    saveToken(token.value);
                }
            });

            PushNotifications.addListener('registrationError', (error) => {
                console.error('Push registration error: ', JSON.stringify(error));
            });

            PushNotifications.addListener('pushNotificationReceived', (notification) => {
                // Suppress active in-app popups/toasts when the app is active
            });

            PushNotifications.addListener('pushNotificationActionPerformed', async (notification) => {
                const data = notification.notification.data;
                const propertyId = data?.property_id;
                let actionLink = data?.action_link || data?.actionLink;

                if (actionLink === "/plants") actionLink = "/home-records?popup=plants";
                if (actionLink === "/vault") actionLink = "/home-records?popup=appliances";

                if (propertyId && profile?.id) {
                    // Set active property locally
                    localStorage.setItem(`homeezi_active_property_${profile.id}`, propertyId);

                    // Sync in background to Supabase profiles table
                    try {
                        await supabase
                            .from("profiles")
                            .update({ active_property_id: propertyId })
                            .eq("id", profile.id);
                    } catch (e) {
                        console.error("Failed to sync active property from push click:", e);
                    }
                }

                if (actionLink) {
                    setTimeout(() => {
                        window.location.href = actionLink;
                    }, 100);
                }
            });

            // Register with Apple/Google
            await PushNotifications.register();

        } catch (e) {
            console.error("Native Push Setup Failed:", e);
        }
    }, [isApp, saveToken, profile?.id]);

    // 3. MAIN EFFECT
    useEffect(() => {
        if (profileLoading || !profile) return;

        if (isApp) {
            setupNativePush();
        } else {
            // WEB PUSH LOGIC (Permission prompt only)
            const setupWebPush = async () => {
                if (!("Notification" in window)) return;
                
                if (Notification.permission === "default") {
                    const lastPrompt = localStorage.getItem("last_notification_prompt");
                    const now = Date.now();
                    const THREE_DAYS = 3 * 24 * 60 * 60 * 1000;

                    if (!lastPrompt || (now - parseInt(lastPrompt) > THREE_DAYS)) {
                        toast("Enable Notifications?", {
                            description: "Stay updated on maintenance and watering schedules.",
                            action: {
                                label: "Enable",
                                onClick: () => {
                                    Notification.requestPermission().then(permission => {
                                        if (permission === "granted") {
                                            toast.success("Notifications enabled!");
                                        }
                                    });
                                }
                            },
                        });
                        localStorage.setItem("last_notification_prompt", now.toString());
                    }
                }
            };
            setupWebPush();
        }

        return () => {
            if (isApp) {
                try {
                    PushNotifications.removeAllListeners();
                } catch (e) {
                    console.error("Failed to remove push listeners:", e);
                }
            }
        };
    }, [profile, profileLoading, isApp, setupNativePush]);
};
